import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useMemo } from "react";
import { FaCheck, FaCheckCircle, FaExclamationCircle, FaMinus, FaTimes, FaTimesCircle } from "react-icons/fa";
import { useListReceivedBidsQuery } from "../../../../../../autogen/bff-api";
import { ProductGroup } from "../../../../../../common/types";
import { useAIEvaluation } from "./useAIEvaluation";
import { useRecommendationTag } from "./useRecommendationTag";

export const SingleBidEvaluator = ({
  bidId,
  sourcingEventId,
  negotiationRoundId,
  productGroups,
}: {
  bidId: string;
  sourcingEventId: string;
  negotiationRoundId?: string;
  productGroups?: ProductGroup[];
}) => {
  const getRecommendationTag = useRecommendationTag();

  const { data: bidsResponse } = useListReceivedBidsQuery({
    eventId: sourcingEventId,
    negotiationRound: negotiationRoundId,
  });

  const bids = useMemo(() => bidsResponse?.bids ?? [], [bidsResponse?.bids]);

  const getFulfillmentIcon = (fulfilled: "yes" | "no" | "partly") => {
    switch (fulfilled) {
      case "yes":
        return FaCheck;
      case "partly":
        return FaMinus;
      case "no":
        return FaTimes;
    }
  };

  const getFulfillmentColor = (fulfilled: "yes" | "no" | "partly") => {
    switch (fulfilled) {
      case "yes":
        return "smSuccess";
      case "partly":
        return "smWarning";
      case "no":
        return "smError";
    }
  };

  const { individualEvaluations, comparison, isLoading, error } = useAIEvaluation({
    sourcingEventId,
    negotiationRoundId,
    bids,
    productGroups,
  });

  const evaluation = useMemo(() => {
    return individualEvaluations.find((evaluation) => evaluation.bidId === bidId);
  }, [bidId, individualEvaluations]);

  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="5" />
        <SkeletonText mt="4" noOfLines={4} spacing="4" skeletonHeight="2" />
        <Skeleton height="5" />
        <SkeletonText mt="4" noOfLines={3} spacing="4" skeletonHeight="2" />
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {error && (
        <Alert status="error">
          <AlertIcon />
          <Text>{`${t("Error evaluating bid with AI")}.`}</Text>
        </Alert>
      )}
      {evaluation && (
        <VStack spacing={4} align="stretch">
          <Box p={4} bg="smBackground" border="1px solid" borderColor="smBorder" borderRadius="md">
            <HStack spacing={6} flexWrap="wrap" align="flex-start">
              <Stat>
                <StatLabel>{t("Score")}</StatLabel>
                <StatNumber
                  color={evaluation.score > 80 ? "smSuccess" : evaluation.score > 60 ? "smWarning" : "smDanger"}
                >
                  {evaluation.score}/100
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Recommendation")}</StatLabel>
                <Box pt="2">{getRecommendationTag(evaluation.recommendation)}</Box>
              </Stat>
              <Stat>
                <StatLabel>{t("Requirements compliance")}</StatLabel>
                <Flex alignItems="center" columnGap="1" pt="2">
                  <Icon as={FaCheckCircle} color="smSuccess" />
                  <Text as="span" color="smSuccess" mr="2">
                    {evaluation.requirements.filter((r) => r.fulfilled === "yes").length}
                  </Text>
                  <Icon as={FaExclamationCircle} color="smWarning" />
                  <Text as="span" color="smWarning" mr="2">
                    {evaluation.requirements.filter((r) => r.fulfilled === "partly").length}
                  </Text>
                  <Icon as={FaTimesCircle} color="smDanger" />
                  <Text as="span" color="smDanger" mr="2">
                    {evaluation.requirements.filter((r) => r.fulfilled === "no").length}
                  </Text>
                </Flex>
              </Stat>
              {typeof evaluation.totalPrice === "number" && evaluation.totalPrice > 0 && (
                <Stat>
                  <StatLabel>{t("Total price")}</StatLabel>
                  <StatNumber>{evaluation.totalPrice.toLocaleString()}</StatNumber>
                </Stat>
              )}
            </HStack>
          </Box>
          <Box backgroundColor="smSecondarySoft" border="1px solid" borderColor="smBorder" borderRadius="md" p="4">
            <Text fontWeight="semibold" mb={2}>
              {t("Executive summary")}
            </Text>
            <VStack spacing="1">
              <Text>{comparison?.perBidSummary?.find((summary) => summary.bidId === bidId)?.comparisonSummary}</Text>
            </VStack>
          </Box>
          <Box bg="smBackground" border="1px solid" borderColor="smBorder" borderRadius="md" p="4">
            <Text fontWeight="semibold" mb={2}>
              {t("Strengths & weaknesses")}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="semibold" mb={1}>
                  {t("Strengths")}
                </Text>
                <VStack spacing={1} align="stretch">
                  {evaluation.strengths.map((strength, i) => (
                    <HStack key={i} spacing={2}>
                      <Icon as={FaCheck} color="smSuccess" boxSize={3} />
                      <Text fontSize="sm">{strength}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb={1}>
                  {t("Weaknesses")}
                </Text>
                <VStack spacing={1} align="stretch">
                  {evaluation.weaknesses.map((weakness, i) => (
                    <HStack key={i} spacing={2}>
                      <Icon as={FaTimes} color="smError" boxSize={3} />
                      <Text fontSize="sm">{weakness}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>
          <Box p={4} bg="smBackground" border="1px solid" borderColor="smBorder" borderRadius="md">
            <Text fontWeight="semibold" mb={2}>{`${t("Requirements Fulfillment")}:`}</Text>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th position="sticky" top={0} bg="smBackground">{`${t("Requirement")}`}</Th>
                  <Th position="sticky" top={0} bg="smBackground" textAlign="center">{`${t("Status")}`}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {evaluation.requirements.map((req, i) => (
                  <Tr key={i}>
                    <Td fontSize="sm">{req.requirement}</Td>
                    <Td textAlign="center">
                      <Icon
                        as={getFulfillmentIcon(req.fulfilled)}
                        color={getFulfillmentColor(req.fulfilled)}
                        boxSize={4}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Box p={4} bg="smBackground" border="1px solid" borderColor="smBorder" borderRadius="md">
            <Text fontWeight="semibold" mb={2}>{`${t("Comments")}:`}</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {evaluation.comments}
            </Text>
          </Box>
        </VStack>
      )}
    </VStack>
  );
};
